import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal, SectionTag } from "@/components/primitives";
import { useLanguage } from "@/hooks/use-language";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full h-12 px-4 bg-transparent border border-border text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:border-teal-500 transition-colors duration-300";

export const ContactForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      toast.success(t.contact.toastOk);
      setForm({ name: "", company: "", email: "", message: "" });
    } catch {
      toast.error(t.contact.toastErr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <SectionTag index="08" label={t.contact.tag} />
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl">
            {t.contact.h2}
          </h2>
          <p className="mt-6 max-w-md text-base md:text-lg text-muted-foreground">
            {t.contact.sub}
          </p>
          <div className="mt-12 space-y-4 text-sm text-muted-foreground">
            {t.contact.bullets.map((b) => (
              <p key={b} className="flex items-center gap-3"><span className="w-1.5 h-1.5 rotate-45 bg-teal-500 shrink-0" /> {b}</p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="border border-border p-8 lg:p-10 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder={t.contact.name}
                className={inputCls}
                data-testid="contact-name-input"
              />
              <input
                required
                value={form.company}
                onChange={set("company")}
                placeholder={t.contact.company}
                className={inputCls}
                data-testid="contact-company-input"
              />
            </div>
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder={t.contact.email}
              className={inputCls}
              data-testid="contact-email-input"
            />
            <textarea
              value={form.message}
              onChange={set("message")}
              placeholder={t.contact.message}
              rows={5}
              className="w-full px-4 py-3 bg-transparent border border-border text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:border-teal-500 transition-colors duration-300 resize-none"
              data-testid="contact-message-input"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="group w-full h-12 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-green-500 text-[#0a0a0a] font-semibold text-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : (
                <>
                  {t.contact.submit}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </>
              )}
            </button>
            <p className="text-xs text-muted-foreground/70">
              {t.contact.note}
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
